import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Flex,
  Input,
  message,
  Popover,
  Skeleton,
  Table,
  TableProps,
  Tooltip,
  Typography
} from 'antd';
import { t } from 'i18next';
import { Brief, DataChannel, DataFactor, Period } from '../../../../../api/types';
import { api } from '../../../../../api/apiProvider';
import urls from '../../../../../api/urls';
import { CaretRightOutlined, EditOutlined, SaveOutlined } from '@ant-design/icons';
import ButtonCompare from '../../../../ui/buttons/ButtonCompare';
import { DataTotalsFilterInterface } from '../useDataTotals';
import useHistoricDataTotal from './useHistoricDataTotal';
import dayjs, { Dayjs } from 'dayjs';
import { useEngine } from '../../../../../hooks/useEngine';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { TableMetrics } from '../filter/TableMetricsFilter';
import { renderNumberCell } from '../../../../../numberFormat';
import HalfYearPicker from '../../../../ui/HalfYearPicker';
import {
  getDefaultHistoricFilter,
  HistoricDateRange,
  HistoricPeriodValue,
  isCustomRangeDateDisabled,
  isQuarterDisabled,
  isSemesterDisabled,
  periodValueToDateRange,
} from './historicPeriodFilter';

dayjs.extend(quarterOfYear);

const { RangePicker } = DatePicker;

const { Text } = Typography;

type OnChange = NonNullable<TableProps<DataType>['onChange']>
type GetSingle<T> = T extends (infer U)[] ? U : never
type Sorts = GetSingle<Parameters<OnChange>[2]>

type DataField = 'budget' | 'kpi' | 'roas' | 'cac'

type TableMetric = keyof TableMetrics

interface DataTotalProps {
  engineId: number,
  period: Period,
  brief: Brief,
  filter: DataTotalsFilterInterface,
  selectedForComparesIds: string[],
  setSelectedForCompares: Function,
  setHistoricData: Function,
  selectedMetrics: TableMetric[],
}

interface DataType {
  name: string | JSX.Element,
  budget: string | JSX.Element,
  cac: string | JSX.Element,
  kpi: string | JSX.Element,
  cumulative: string | JSX.Element,
  roas: string | JSX.Element,
  isTotal: boolean,
  headertype: string,
}

interface TableData {
  name: string | JSX.Element,
  budget: string | JSX.Element,
  cac: string | JSX.Element,
  kpi: string | JSX.Element,
  kpi_long_term: string | JSX.Element,
  kpi_short_term: string | JSX.Element,
  cumulative: string | JSX.Element,
  roas: string | JSX.Element,
  id: string,
  audienceText: string,
  managerText: string,
  isTotal: boolean,
  headertype: string,
}

const mediaType = 'media';
const nonMediaType = 'nonMedia';

export const HISTORIC_DATA_TABLE = 'historic_data_table';

const updateName = async (id: number, name: string) => {
  return await api.patch(`${urls.engines}/${id}`, { arg: { name } });
};

export default function HistoricDataTotalTable ({
  engineId,
  period,
  brief,
  filter,
  selectedForComparesIds,
  setSelectedForCompares,
  setHistoricData,
  selectedMetrics,
}: DataTotalProps) {
  const [isEditingName, setIsEditingName] = useState<boolean>(false);
  const [isNameUpdating, setIsNameUpdating] = useState<boolean>(false);
  const [isMediaExpanded, setIsMediaExpanded] = useState<boolean>(true);
  const [isNonMediaExpanded, setIsNonMediaExpanded] = useState<boolean>(false);
  const [sorter, setSorter] = useState<Sorts>({});
  const [name, setName] = useState<string>('');
  const [periodValue, setPeriodValue] = useState<HistoricPeriodValue | undefined>(undefined);
  const [defaultDateRange, setDefaultDateRange] = useState<HistoricDateRange | undefined>(undefined);
  const { engine } = useEngine();

  const dateRange = useMemo(() => {
    if (periodValue === undefined) {
      return undefined;
    }

    const fromPicker = periodValueToDateRange(period.periodLength, periodValue);

    if (fromPicker) {
      return fromPicker;
    }

    return defaultDateRange;
  }, [period.periodLength, periodValue, defaultDateRange]);

  useEffect(() => {
    if (!engine?.historicDateStart || !engine?.historicDateEnd || !period.dateStart || !period.dateEnd) {
      return;
    }

    const defaults = getDefaultHistoricFilter(
      period.periodLength,
      dayjs(period.dateStart),
      dayjs(period.dateEnd),
      dayjs(engine.historicDateStart),
      dayjs(engine.historicDateEnd),
    );

    setPeriodValue(defaults.periodValue);
    setDefaultDateRange(defaults.dateRange);
  }, [
    engine?.historicDateStart,
    engine?.historicDateEnd,
    period.periodLength,
    period.dateStart,
    period.dateEnd,
  ]);

  const {
    historicDataTotal,
    mutateHistoricDataTotal,
    isHistoricDataTotalLoading
  } = useHistoricDataTotal(engine, filter, dateRange);

  useEffect(() => {
    if (historicDataTotal) {
      if (isEditingName) {
        setName(historicDataTotal.name);
      } else {
        const startDate = dayjs(historicDataTotal.dateStart).format(t('common.dateFormat'));
        const endDate = dayjs(historicDataTotal.dateEnd).format(t('common.dateFormat'));

        setName(`${historicDataTotal?.name || ''} (${startDate} - ${endDate})`);
      }

      setHistoricData(historicDataTotal);
    }
  }, [historicDataTotal, isEditingName]);

  const data = useMemo(() => {
    if (!historicDataTotal) {
      return [];
    }

    let dataChannels: DataChannel[] = historicDataTotal.dataChannels;

    if (sorter.columnKey && sorter.order) {
      const key: DataField = String(sorter.columnKey) as DataField;
      const order = String(sorter.order);

      dataChannels.sort((a, b) =>
        order === 'ascend' ? (Number(a.metrics[key]) - Number(b.metrics[key])) : (Number(b.metrics[key]) - Number(a.metrics[key])));
    }

    const data: TableData[] = [];

    if (dataChannels.length > 0) {
      data.push({
        name: <Flex
          justify={'space-between'}
          align={'center'}
          className={'data-total__group-title' + (isMediaExpanded ? ' expanded' : '')}
          onClick={() => setIsMediaExpanded(!isMediaExpanded)}
        >
          <Flex>{t('planner.media')} ({dataChannels.length})</Flex>
          <Flex>
            <span style={{ color: '#7E8681' }}><CaretRightOutlined rotate={isMediaExpanded ? 90 : 0}/></span>
          </Flex>
        </Flex>,
        isTotal: false,
        budget: renderNumberCell(historicDataTotal.metrics?.media?.budget),
        cac: renderNumberCell(historicDataTotal.metrics?.media?.cac),
        kpi: renderNumberCell(historicDataTotal.metrics?.media?.kpi),
        kpi_short_term: renderNumberCell(historicDataTotal.metrics?.media?.kpi_short_term),
        kpi_long_term: renderNumberCell(historicDataTotal.metrics?.media?.kpi_long_term),
        cumulative: renderNumberCell(historicDataTotal.metrics?.media?.cumulative),
        roas: renderNumberCell(historicDataTotal.metrics?.media?.roas, 1),
        id: 'media_group',
        audienceText: '',
        managerText: '',
        headertype: mediaType,
      });
    }

    if (isMediaExpanded) {
      for (let dataChannel of dataChannels) {
        data.push({
          name: dataChannel.name,
          budget: renderNumberCell(dataChannel.metrics?.budget),
          cac: renderNumberCell(dataChannel.metrics?.cac),
          kpi: renderNumberCell(dataChannel.metrics?.kpi),
          kpi_short_term: renderNumberCell(dataChannel.metrics?.kpi_short_term),
          kpi_long_term: renderNumberCell(dataChannel.metrics?.kpi_long_term),
          cumulative: renderNumberCell(null),
          roas: renderNumberCell(dataChannel.metrics?.roas, 1),
          id: dataChannel['@id'],
          audienceText: dataChannel?.channel?.audienceText || '',
          managerText: dataChannel?.channel?.managerText || '',
          isTotal: false,
          headertype: '',
        });
      }

      data.push({
        name: t('planner.allMedia'),
        budget: renderNumberCell(historicDataTotal.metrics?.media?.budget),
        cac: renderNumberCell(historicDataTotal.metrics?.media?.cac),
        kpi: renderNumberCell(historicDataTotal.metrics?.media?.kpi),
        kpi_short_term: renderNumberCell(historicDataTotal.metrics?.media?.kpi_short_term),
        kpi_long_term: renderNumberCell(historicDataTotal.metrics?.media?.kpi_long_term),
        cumulative: renderNumberCell(historicDataTotal.metrics?.media?.cumulative),
        roas: renderNumberCell(historicDataTotal.metrics?.media?.roas, 1),
        id: 'total_media',
        audienceText: '',
        managerText: '',
        isTotal: false,
        headertype: '',
      });
    }

    let dataFactors: DataFactor[] = historicDataTotal.nonMediaDataFactors
      ? historicDataTotal.nonMediaDataFactors
      : [];

    if (sorter.columnKey && sorter.order) {
      const key: DataField = String(sorter.columnKey) as DataField;
      const order = String(sorter.order);

      dataFactors.sort((a, b) =>
        order === 'ascend' ? (Number(a?.metrics?.[key]) - Number(b?.metrics?.[key])) : (Number(b?.metrics?.[key]) - Number(a?.metrics?.[key])));
    }

    if (dataFactors.length > 0) {
      data.push({
        name: <Flex
          justify={'space-between'}
          align={'center'}
          className={'data-total__group-title' + (isNonMediaExpanded ? ' expanded' : '')}
          onClick={() => setIsNonMediaExpanded(!isNonMediaExpanded)}
        >
          <Flex>{t('planner.nonMedia')} ({dataFactors.length})</Flex>
          <Flex>
            <span style={{ color: '#7E8681' }}><CaretRightOutlined rotate={isNonMediaExpanded ? 90 : 0}/></span>
          </Flex>
        </Flex>,
        isTotal: false,
        budget: renderNumberCell(historicDataTotal.metrics?.nonMedia?.budget),
        cac: renderNumberCell(historicDataTotal.metrics?.nonMedia?.cac),
        kpi: renderNumberCell(historicDataTotal.metrics?.nonMedia?.kpi),
        kpi_short_term: renderNumberCell(historicDataTotal.metrics?.nonMedia?.kpi_short_term),
        kpi_long_term: renderNumberCell(historicDataTotal.metrics?.nonMedia?.kpi_long_term),
        cumulative: renderNumberCell(historicDataTotal.metrics?.media?.cumulative),
        roas: renderNumberCell(historicDataTotal.metrics?.nonMedia?.roas, 1),
        id: 'non_media_group',
        audienceText: '',
        managerText: '',
        headertype: nonMediaType,
      });
    }

    if (isNonMediaExpanded) {
      for (let dataFactor of dataFactors) {
        data.push({
          name: dataFactor.factor.name,
          budget: renderNumberCell(dataFactor.metrics?.budget),
          cac: renderNumberCell(dataFactor.metrics?.cac),
          kpi: renderNumberCell(dataFactor.metrics?.kpi),
          kpi_short_term: renderNumberCell(dataFactor.metrics?.kpi_short_term),
          kpi_long_term: renderNumberCell(dataFactor.metrics?.kpi_long_term),
          cumulative: renderNumberCell(null),
          roas: renderNumberCell(dataFactor.metrics?.roas, 1),
          id: dataFactor['@id'],
          audienceText: dataFactor?.factor?.audienceText || '',
          managerText: dataFactor?.factor?.managerText || '',
          isTotal: false,
          headertype: '',
        });
      }

      data.push({
        name: t('planner.allNonMedia'),
        budget: renderNumberCell(historicDataTotal.metrics?.nonMedia?.budget),
        cac: renderNumberCell(historicDataTotal.metrics?.nonMedia?.cac),
        kpi: renderNumberCell(historicDataTotal.metrics?.nonMedia?.kpi),
        kpi_short_term: renderNumberCell(historicDataTotal.metrics?.nonMedia?.kpi_short_term),
        kpi_long_term: renderNumberCell(historicDataTotal.metrics?.nonMedia?.kpi_long_term),
        cumulative: renderNumberCell(historicDataTotal.metrics?.nonMedia?.cumulative),
        roas: renderNumberCell(historicDataTotal.metrics?.nonMedia?.roas, 1),
        id: 'total_non_media',
        audienceText: '',
        managerText: '',
        isTotal: false,
        headertype: '',
      });
    }

    if (!filter || !filter.channels || (historicDataTotal.dataChannels.length === filter.channels.length || filter.channels.length === 0)) {
      data.push({
        name: t('planner.total'),
        budget: renderNumberCell(historicDataTotal.metrics?.total?.budget),
        cac: renderNumberCell(historicDataTotal.metrics?.total?.cac),
        kpi: renderNumberCell(historicDataTotal.metrics?.total?.kpi),
        kpi_short_term: renderNumberCell(historicDataTotal.metrics?.total?.kpi_short_term),
        kpi_long_term: renderNumberCell(historicDataTotal.metrics?.total?.kpi_long_term),
        cumulative: renderNumberCell(historicDataTotal.metrics?.total?.cumulative),
        roas: renderNumberCell(historicDataTotal.metrics?.total?.roas, 1),
        id: HISTORIC_DATA_TABLE,
        audienceText: '',
        managerText: '',
        isTotal: true,
        headertype: '',
      });
    }

    return data;
  }, [historicDataTotal, filter, sorter, isMediaExpanded, isNonMediaExpanded]);

  const saveName = () => {
    if (!historicDataTotal) {
      return;
    }

    setIsNameUpdating(true);

    updateName(engineId, name)
      .then(async () => {
        await mutateHistoricDataTotal();
        setIsEditingName(false);
        setIsNameUpdating(false);
      }).catch((response) => {
      message.error(response['hydra:description']);
      setIsEditingName(false);
      setName(historicDataTotal?.name);
      setIsNameUpdating(false);
    });
  };

  const handleCompareClick = () => {
    if (!historicDataTotal) {
      return;
    }

    if (selectedForComparesIds.includes(HISTORIC_DATA_TABLE)) {
      setSelectedForCompares([...selectedForComparesIds.filter(function (compareItemId, index, arr) {
        return compareItemId !== HISTORIC_DATA_TABLE;
      })]);
    } else {

      setSelectedForCompares([
        ...selectedForComparesIds,
        HISTORIC_DATA_TABLE,
      ]);
    }
  };

  const isInCompare = () => {
    return selectedForComparesIds.includes(HISTORIC_DATA_TABLE);
  };

  const isMediaGroupExpanded = (record: DataType) => record.headertype === mediaType && isMediaExpanded;
  const isNonMediaGroupExpanded = (record: DataType) => record.headertype === nonMediaType && isNonMediaExpanded;

  const columns: TableProps<DataType>['columns'] = [
    {
      title: t('planner.channel'),
      dataIndex: 'name',
      key: 'name',
      width: '20%',
      onCell: (record: DataType, index) => {
        if (isMediaGroupExpanded(record) || isNonMediaGroupExpanded(record)) {
          return { colSpan: 9 };
        }
        return {};
      },
    },
    {
      title: <Popover placement="top" content={
        <div>{t('planner.kpiInTotalsFull')}</div>
      } arrow overlayClassName={'sider-links__popover'}>
        {t('planner.kpiInTotals')}
      </Popover>,
      dataIndex: 'kpi',
      key: 'kpi',
      width: '15%',
      sorter: {
        compare: (a, b) => Number(a.kpi) - Number(b.kpi),
      },
      sortOrder: sorter.columnKey === 'kpi' ? sorter.order : null,
      onCell: (record, index) => {
        if (isMediaGroupExpanded(record) || isNonMediaGroupExpanded(record)) {
          return { colSpan: 0 };
        }

        return {};
      },
      hidden: !selectedMetrics.includes('kpi'),
    },
    {
      title: <Popover placement="top" content={
        <div>{t('planner.kpiShortTermFull')}</div>
      } arrow overlayClassName={'sider-links__popover'}>
        {t('planner.kpiShortTerm')}
      </Popover>,
      dataIndex: 'kpi_short_term',
      key: 'kpi_short_term',
      width: '15%',
      sorter: {
        compare: (a, b) => Number(a.kpi) - Number(b.kpi),
      },
      sortOrder: sorter.columnKey === 'kpi' ? sorter.order : null,
      onCell: (record, index) => {
        if (isMediaGroupExpanded(record) || isNonMediaGroupExpanded(record)) {
          return { colSpan: 0 };
        }

        return {};
      },
      hidden: !selectedMetrics.includes('kpi_short_term'),
    },
    {
      title: <Popover placement="top" content={
        <div>{t('planner.kpiLongTermFull')}</div>
      } arrow overlayClassName={'sider-links__popover'}>
        {t('planner.kpiLongTerm')}
      </Popover>,
      dataIndex: 'kpi_long_term',
      key: 'kpi_long_term',
      width: '15%',
      sorter: {
        compare: (a, b) => Number(a.kpi) - Number(b.kpi),
      },
      sortOrder: sorter.columnKey === 'kpi' ? sorter.order : null,
      onCell: (record, index) => {
        if (isMediaGroupExpanded(record) || isNonMediaGroupExpanded(record)) {
          return { colSpan: 0 };
        }

        return {};
      },
      hidden: !selectedMetrics.includes('kpi_long_term'),
    },
    {
      title: t('planner.cumulative'),
      dataIndex: 'cumulative',
      key: 'cumulative',
      width: '15%',
      sorter: {
        compare: (a, b) => Number(a.cumulative) - Number(b.cumulative),
      },
      sortOrder: sorter.columnKey === 'cumulative' ? sorter.order : null,
      onCell: (record: DataType, index) => {
        if (isMediaGroupExpanded(record) || isNonMediaGroupExpanded(record)) {
          return { colSpan: 0 };
        }
        return {};
      },
      hidden: !selectedMetrics.includes('cumulative'),
    },
    {
      title: t('planner.budget'),
      dataIndex: 'budget',
      key: 'budget',
      width: '15%',
      sorter: {
        compare: (a, b) => Number(a.budget) - Number(b.budget),
      },
      sortOrder: sorter.columnKey === 'budget' ? sorter.order : null,
      onCell: (record: DataType, index) => {
        if (isMediaGroupExpanded(record) || isNonMediaGroupExpanded(record)) {
          return { colSpan: 0 };
        }
        return {};
      },
      hidden: !selectedMetrics.includes('budget'),
    },
    /*{
      label: t('planner.metrics'),
      code: 'metrics',
    },*/
    {
      title: t('planner.roas'),
      dataIndex: 'roas',
      key: 'roas',
      width: '15%',
      sorter: {
        compare: (a, b) => Number(a.roas) - Number(b.roas),
      },
      sortOrder: sorter.columnKey === 'roas' ? sorter.order : null,
      onCell: (record: DataType, index) => {
        if (isMediaGroupExpanded(record) || isNonMediaGroupExpanded(record)) {
          return { colSpan: 0 };
        }
        return {};
      },
      hidden: !selectedMetrics.includes('roas'),
    },
    /*{
      title: t('planner.cac'),
      dataIndex: 'cac',
      key: 'cac',
      width: '15%',
      sorter: {
        compare: (a, b) => Number(a.cac) - Number(b.cac),
      },
      sortOrder: sorter.columnKey === 'cac' ? sorter.order : null,
    },*/
    {
      title: t('factors.audienceText'),
      dataIndex: 'audienceText',
      key: 'audienceText',
      width: '10%',
      onCell: (record: DataType, index) => {
        if (isMediaGroupExpanded(record) || isNonMediaGroupExpanded(record)) {
          return { colSpan: 0 };
        }
        return {};
      },
      hidden: !selectedMetrics.includes('audienceText'),
    },
    {
      title: t('factors.managerText'),
      dataIndex: 'managerText',
      key: 'managerText',
      width: '10%',
      onCell: (record: DataType, index) => {
        if (isMediaGroupExpanded(record) || isNonMediaGroupExpanded(record)) {
          return { colSpan: 0 };
        }
        return {};
      },
      hidden: !selectedMetrics.includes('managerText'),
    },
  ];

  const onChange: TableProps<DataType>['onChange'] = (pagination, filters, sorter, extra) => {
    setSorter(sorter as Sorts);
  };

  const historicBounds = useMemo(() => {
    if (!engine?.historicDateStart || !engine?.historicDateEnd) {
      return null;
    }

    return {
      start: dayjs(engine.historicDateStart),
      end: dayjs(engine.historicDateEnd),
    };
  }, [engine?.historicDateStart, engine?.historicDateEnd]);

  const disabledQuarterDate = useCallback((current: Dayjs | null) => {
    if (!historicBounds || !current) {
      return false;
    }

    return isQuarterDisabled(current, historicBounds.start, historicBounds.end);
  }, [historicBounds]);

  const disabledSemesterDate = useCallback((current: Dayjs | null) => {
    if (!historicBounds || !current) {
      return false;
    }

    return isSemesterDisabled(current, historicBounds.start, historicBounds.end);
  }, [historicBounds]);

  const disabledCustomDate = useCallback((current: Dayjs | null, info?: { from?: Dayjs }) => {
    if (!historicBounds || !current) {
      return false;
    }

    return isCustomRangeDateDisabled(
      current,
      info ?? {},
      historicBounds.start,
      historicBounds.end,
      engine?.isWeekInterval ?? false,
    );
  }, [historicBounds, engine?.isWeekInterval]);

  const periodPicker = useMemo(() => {
    if (period.periodLength === 1) {
      return (
        <RangePicker
          size="small"
          placeholder={[t('list.dateStart'), t('list.dateEnd')]}
          format={t('common.dateFormat')}
          value={periodValue as [Dayjs, Dayjs] | null | undefined}
          onChange={(value) => setPeriodValue(value as [Dayjs, Dayjs] | null)}
          disabledDate={disabledCustomDate}
          allowClear
        />
      );
    }

    if (period.periodLength === 2) {
      return (
        <HalfYearPicker
          size="small"
          value={periodValue as Dayjs | null | undefined}
          onChange={(value) => setPeriodValue(value as Dayjs | null)}
          disabledDate={disabledSemesterDate}
          allowClear
        />
      );
    }

    return (
      <DatePicker
        format={(date) => {
          if (date) {
            const quarterNum = Math.floor(date.month() / 3) + 1;
            const year = date.year();
            return `Q${quarterNum} ${year}`;
          }
          return '';
        }}
        size="small"
        value={periodValue as Dayjs | null | undefined}
        onChange={(value) => setPeriodValue(value)}
        disabledDate={disabledQuarterDate}
        allowClear
        picker="quarter"
      />
    );
  }, [
    period.periodLength,
    periodValue,
    disabledCustomDate,
    disabledSemesterDate,
    disabledQuarterDate,
  ]);

  return (
    <Card
      title={!historicDataTotal
        ? <Flex justify={'space-between'}>
          <Skeleton.Input active size={'small'} style={{ width: '40%' }} block/>
          <Skeleton.Input active size={'small'}/>
        </Flex>
        : <Flex style={{ width: '100%' }} gap={'small'} align={'center'} justify={'space-between'}>
          <Flex gap={'small'}>
            <Flex gap={'small'}>
              {!isEditingName &&
                <Text
                  style={{ maxWidth: 300 }}
                  ellipsis={{ tooltip: name }}
                >
                  {name}
                </Text>
              }
              {isEditingName &&
                <Input
                  value={name}
                  onChange={e => {
                    setName(e.target.value);
                  }}
                  size={'small'}
                  style={{ minWidth: '300px', flex: 1 }}
                />
              }

              {!isEditingName &&
                <>
                  <Tooltip title={t('common.edit')}>
                    <Button
                      icon={<EditOutlined/>}
                      size={'small'}
                      onClick={() => setIsEditingName(true)}
                    ></Button>
                  </Tooltip>
                </>
              }

              {isEditingName &&
                <Tooltip title={t('common.save')}>
                  <Button
                    icon={<SaveOutlined/>}
                    size={'small'}
                    onClick={() => saveName()}
                    loading={isNameUpdating || isHistoricDataTotalLoading}
                  ></Button>
                </Tooltip>
              }
            </Flex>
            <Flex>
              {periodPicker}
            </Flex>
          </Flex>

          <Flex align={'center'}>
            <ButtonCompare
              size={'small'}
              compared={isInCompare()}
              onClick={handleCompareClick}
            />
          </Flex>
        </Flex>
      }
      classNames={{
        body: 'data-total__card-body'
      }}
    >
      <Table
        className={'variant-table'}
        style={{ width: '100%' }}
        columns={columns}
        dataSource={data}
        pagination={false}
        bordered
        rowKey={'id'}
        onChange={onChange}
        rowClassName={(record: DataType) => record.isTotal ? 'total-row' : ''}
        loading={isHistoricDataTotalLoading}
      />
    </Card>
  );
};
